import { type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * Render Markdown to React. Sanitised by react-markdown by default (no raw HTML).
 * Applies the .prose typography wrapper from globals.css.
 */
export function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
  return (
    <div className={cn('prose', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ node: _node, ...props }) => (
            <a {...props} target={props.href?.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" />
          ),
          pre: ({ children }) => <pre>{children}</pre>,
          img: ({ node: _node, ...props }) => (
            // eslint-disable-next-line @next/next/no-img-element
            <img loading="lazy" decoding="async" {...props} alt={props.alt ?? ''} />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

/**
 * Headings extractor for table-of-contents.
 * Returns id (slugified) and text for each H2/H3 in the markdown.
 */
export function extractHeadings(markdown: string): Array<{ level: 2 | 3; text: string; id: string }> {
  const lines = markdown.split('\n');
  const headings: Array<{ level: 2 | 3; text: string; id: string }> = [];
  let inFence = false;
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^```/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const m = /^(##|###)\s+(.+?)\s*#*\s*$/.exec(line);
    if (!m || !m[1] || !m[2]) continue;
    const level = (m[1].length === 2 ? 2 : 3) as 2 | 3;
    const text = m[2].trim();
    const id = slugifyHeading(text);
    headings.push({ level, text, id });
  }
  return headings;
}

function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

/**
 * Slugify each heading by injecting an id attribute via the rehype-style heading rewrite.
 * Used to wire a sticky table-of-contents (TableOfContents.tsx) into the rendered output.
 */
export function MarkdownWithAnchors({
  content,
  className,
}: MarkdownRendererProps): ReactNode {
  return (
    <div className={cn('prose', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h2: ({ children, ...rest }) => {
            const text = String(children ?? '');
            return <h2 id={slugifyHeading(text)} {...rest}>{children}</h2>;
          },
          h3: ({ children, ...rest }) => {
            const text = String(children ?? '');
            return <h3 id={slugifyHeading(text)} {...rest}>{children}</h3>;
          },
          a: ({ node: _node, ...props }) => (
            <a {...props} target={props.href?.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" />
          ),
          pre: ({ children }) => <pre>{children}</pre>,
          img: ({ node: _node, ...props }) => (
            // eslint-disable-next-line @next/next/no-img-element
            <img loading="lazy" decoding="async" {...props} alt={props.alt ?? ''} />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}